import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, Box } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { DomainTabProps, TabBody, errorText } from './shared';

interface Item { id: string; operation: string; status: string; summary: string | null; cost: number | null; actor: string; error: string | null; created_at: string | null }

const ActivityTab: React.FC<DomainTabProps> = ({ serviceId }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems((await apiService.domainRequest<{ items: Item[] }>('get', serviceId, '/activity')).items || []);
    } catch (e) {
      setError(errorText(e, 'Could not load the activity.'));
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <TabBody>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">Every change made to this domain from SonOps, with who made it.</Typography>
        <Button size="small" variant="outlined" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={load} disabled={loading}>Reload</Button>
      </Box>
      <Paper variant="outlined" sx={{ overflow: 'auto', borderRadius: 0 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>What</TableCell><TableCell>Result</TableCell><TableCell align="right">Cost</TableCell></TableRow></TableHead>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>{loading ? 'Loading…' : 'No changes recorded yet.'}</TableCell></TableRow>}
            {items.map((i) => (
              <TableRow key={i.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.created_at ? new Date(i.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</TableCell>
                <TableCell>{i.actor}</TableCell>
                <TableCell>{i.summary || i.operation}{i.error && <Typography variant="caption" color="error" display="block">{i.error}</Typography>}</TableCell>
                <TableCell><Chip size="small" label={i.status === 'success' ? 'Done' : 'Failed'} color={i.status === 'success' ? 'success' : 'error'} variant="outlined" /></TableCell>
                <TableCell align="right">{i.cost != null ? `$${i.cost.toFixed(2)}` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </TabBody>
  );
};

export default ActivityTab;
